import { envPublic } from '@/libs/config/env.public';
import type { FirebaseEnv } from '@/libs/firebase/init';
import { getCurrentFirebaseEnv, useFirebaseEnvStore } from '@/stores/firebase-env-store';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

/**
 * Per-env URLs that travel with the selected Firebase project. Only the
 * `production` set is hardcoded here; `development` reads from `.env` (the
 * same source the build already uses), so dev devs don't have to keep this
 * file in sync.
 *
 * Public values — safe to ship in the JS bundle.
 */
const PRODUCTION_URLS = {
  firebaseCloudFunctionUrl: 'https://uskg-gcwa37moma-uc.a.run.app',
  pictureUrl:
    'https://firebasestorage.googleapis.com/v0/b/uskg-prod.appspot.com/o/profile-pictures%2f',
  pinUrl: 'https://firebasestorage.googleapis.com/v0/b/uskg-prod.appspot.com/o/pins%2f',
  usKidGolfUrl: 'https://us-kid-golf-api-1091922297880.us-central1.run.app',
  // Booking-engine backend — coach-directory search for the "Find a nearby coach" step.
  bookingEngineUrl: 'https://booking-engine-api-524266247967.asia-southeast1.run.app',
  // OIDC IdP origin (guardian-view-be). Must be a subdomain of the web app's registrable
  // domain (hnl.works) so its interaction/session cookie is same-site with the web — otherwise
  // Safari (ITP) drops it as a third-party cookie and interactions fail with "not found".
  ssoIssuerUrl: 'https://auth.hnl.works',
} as const;

export type EnvUrls = {
  firebaseCloudFunctionUrl: string;
  pictureUrl: string;
  pinUrl: string;
  usKidGolfUrl: string;
  bookingEngineUrl: string;
  ssoIssuerUrl: string;
};

export const getEnvUrls = (env: FirebaseEnv): EnvUrls => {
  if (env === 'production') return { ...PRODUCTION_URLS };
  return {
    firebaseCloudFunctionUrl: envPublic.firebaseCloudFunctionUrl,
    pictureUrl: envPublic.pictureUrl,
    pinUrl: envPublic.pinUrl,
    usKidGolfUrl: envPublic.usKidGolfApiUrl,
    bookingEngineUrl: envPublic.bookingEngineApiUrl,
    ssoIssuerUrl: envPublic.ssoIssuerUrl,
  };
};

/**
 * Non-hook accessor — useful inside axios interceptors and helpers running
 * outside React. Reads the latest selected env from the store on every call,
 * so toggling env at runtime is picked up immediately.
 */
export const getCurrentEnvUrls = (): EnvUrls => getEnvUrls(getCurrentFirebaseEnv());

/**
 * React hook variant — re-renders when the selected env changes.
 */
export const useEnvUrls = (): EnvUrls => {
  const env = useFirebaseEnvStore(useShallow(s => s.env));
  return useMemo(() => getEnvUrls(env), [env]);
};
